import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Mail, Send, Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty } from "@/components/common";

export default function AdminWaitlist() {
  const [data, setData] = useState(null);
  const [bulk, setBulk] = useState(10);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [working, setWorking] = useState(false);

  const load = () => api.get("/admin/waitlist").then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const invite = async (id) => {
    try {
      const res = await api.post("/admin/waitlist/invite", { id, origin: window.location.origin });
      toast.success(res.email_sent ? `Invited · code ${res.code} · email sent` : `Invited · code ${res.code} · ⚠️ email failed`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const resend = async (id) => {
    try {
      const res = await api.post("/admin/waitlist/resend", { id, origin: window.location.origin });
      toast.success(res.email_sent ? `Invite email resent · code ${res.code}` : `⚠️ Resend failed · code ${res.code}`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const inviteBulkByCount = async () => {
    try {
      const res = await api.post("/admin/waitlist/invite-bulk", { count: Number(bulk), origin: window.location.origin });
      toast.success(`Invited ${res.invited_count} people · ${res.email_sent_count} emails sent`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const q = query.trim().toLowerCase();
  const filtered = data.waitlist.filter((w) =>
    !q || (w.name || "").toLowerCase().includes(q) || (w.email || "").toLowerCase().includes(q)
  );
  const allSelected = filtered.length > 0 && filtered.every((w) => selected.has(w.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(filtered.map((w) => w.id))));

  const selectedRows = filtered.filter((w) => selected.has(w.id));
  const selPending = selectedRows.filter((w) => !w.invited);
  const selInvited = selectedRows.filter((w) => w.invited);

  const bulkAction = async (kind) => {
    const rows = kind === "invite" ? selPending : selInvited;
    if (!rows.length) { toast.error(`No ${kind === "invite" ? "pending" : "invited"} rows selected`); return; }
    setWorking(true);
    let ok = 0, mail = 0;
    for (const w of rows) {
      try {
        const path = kind === "invite" ? "/admin/waitlist/invite" : "/admin/waitlist/resend";
        const res = await api.post(path, { id: w.id, origin: window.location.origin });
        ok++; if (res.email_sent) mail++;
      } catch { /* keep going */ }
    }
    setWorking(false);
    setSelected(new Set());
    toast.success(`${kind === "invite" ? "Invited" : "Resent to"} ${ok} · ${mail} emails sent`);
    load();
  };

  const removeOne = async (id, name) => {
    if (!window.confirm(`Remove ${name || "this signup"} from the waitlist?`)) return;
    try { await api.del(`/admin/waitlist/${id}`); toast.success("Removed from waitlist"); setSelected((s) => { const n = new Set(s); n.delete(id); return n; }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const deleteSelected = async () => {
    if (!selectedRows.length) { toast.error("No rows selected"); return; }
    if (!window.confirm(`Remove ${selectedRows.length} signup(s) from the waitlist? This can't be undone.`)) return;
    setWorking(true);
    try {
      const res = await api.post("/admin/waitlist/delete-bulk", { ids: selectedRows.map((w) => w.id) });
      toast.success(`Removed ${res.deleted} signup(s)`);
    } catch (e) { toast.error(e.message); }
    setWorking(false);
    setSelected(new Set());
    load();
  };

  return (
    <div className="fade-up">
      <PageHead title="Waitlist Admin" sub="Invite people waiting for early access." />
      <div className="ss-grid grid-3" style={{ marginBottom: 20 }}>
        <div className="ss-card stat-card"><div className="stat-ic"><Users size={22} /></div><div><div className="stat-val">{data.stats.total}</div><div className="stat-label">Total on waitlist</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><Mail size={22} /></div><div><div className="stat-val">{data.stats.pending}</div><div className="stat-label">Pending</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><Send size={22} /></div><div><div className="stat-val">{data.stats.invited}</div><div className="stat-label">Invited</div></div></div>
      </div>

      <div className="ss-card" style={{ padding: 16, display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
        <div><label className="ss-label">Bulk invite next N</label><div className="ss-field" style={{ width: 120, height: 44 }}><input type="number" value={bulk} onChange={(e) => setBulk(e.target.value)} data-testid="bulk-count" /></div></div>
        <button className="ss-btn ss-btn-primary" onClick={inviteBulkByCount} data-testid="bulk-invite"><Send size={16} /> Invite next {bulk}</button>
      </div>

      {/* Search + bulk actions on selected rows */}
      <div className="ss-card" style={{ padding: 16, display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <div className="ss-field" style={{ flex: 1, minWidth: 220, height: 44, display: "flex", alignItems: "center", gap: 8, paddingLeft: 12 }}>
          <Search size={16} style={{ opacity: .6 }} />
          <input placeholder="Search name or email…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="waitlist-search" style={{ border: "none", background: "transparent", flex: 1, outline: "none" }} />
        </div>
        <span className="ss-muted" style={{ fontSize: 13 }} data-testid="waitlist-selected-count">{selected.size} selected</span>
        <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => bulkAction("invite")} disabled={working || selPending.length === 0} data-testid="bulk-invite-selected"><Send size={15} /> Invite selected ({selPending.length})</button>
        <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => bulkAction("resend")} disabled={working || selInvited.length === 0} data-testid="bulk-resend-selected"><Send size={15} /> Resend selected ({selInvited.length})</button>
        <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={deleteSelected} disabled={working || selected.size === 0} data-testid="bulk-delete-selected"><Trash2 size={15} /> Delete selected ({selected.size})</button>
      </div>

      {filtered.length === 0 ? (
        <Empty icon={Users} title={q ? "No matches" : "Waitlist is empty"} sub={q ? "Try a different search." : undefined} />
      ) : (
        <div className="ss-card" style={{ overflow: "auto" }}>
          <table className="ss-table" data-testid="waitlist-table">
            <thead><tr>
              <th style={{ width: 40 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="waitlist-select-all" aria-label="Select all" /></th>
              <th>Name</th><th>Email</th><th>Status</th><th style={{ textAlign: "right" }}>Action</th>
            </tr></thead>
            <tbody>
              {filtered.map((w) => (
                <tr key={w.id} style={selected.has(w.id) ? { background: "var(--gold-soft)" } : undefined}>
                  <td><input type="checkbox" checked={selected.has(w.id)} onChange={() => toggle(w.id)} data-testid={`waitlist-select-${w.id}`} aria-label={`Select ${w.name}`} /></td>
                  <td style={{ fontWeight: 700 }}>{w.name}</td>
                  <td>{w.email}</td>
                  <td>{w.invited ? <span className="ss-pill pill--delivered">Invited</span> : <span className="ss-pill pill--open">Pending</span>}</td>
                  <td style={{ textAlign: "right" }}>
                    {!w.invited && <button className="ss-btn ss-btn-sm ss-btn-primary" onClick={() => invite(w.id)} data-testid={`invite-${w.id}`}>Invite</button>}
                    {w.invited && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, justifyContent: "flex-end" }}>
                        <span className="ss-muted" style={{ fontWeight: 800 }} data-testid={`code-${w.id}`}>{w.invite_code}</span>
                        {w.email_opened
                          ? <span className="ss-pill" style={{ background: "var(--gold-soft)", color: "var(--brand)" }} data-testid={`email-status-${w.id}`}>✓ Opened</span>
                          : w.email_status === "failed"
                            ? <span className="ss-pill pill--open" data-testid={`email-status-${w.id}`}>Failed</span>
                            : <span className="ss-pill" style={{ background: "var(--surface-3)", color: "var(--muted)" }} data-testid={`email-status-${w.id}`}>Sent</span>}
                        <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => resend(w.id)} data-testid={`resend-${w.id}`}><Send size={14} /> Resend</button>
                      </div>
                    )}
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)", marginLeft: 8 }} onClick={() => removeOne(w.id, w.name)} data-testid={`delete-${w.id}`} title="Remove from waitlist"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
